import { Select } from 'antd';
import ChartComponent, { CHART_TYPES } from '../../../components/chartComponent/ChartComponent';
import { useEffect, useState, useRef } from 'react';
import FormItem from 'antd/es/form/FormItem';
import { referenceDataSyncService } from '../../../services/referenceDataSyncService';
import type { Basket } from '../../../types/basket';
import type { Location } from '../../../types/referenceData';
import { analysisService } from '../../../api/services/analysisService';
import PrimaryButton from '../../../components/buttons/PrimaryButton';
import { normalize } from '../../../utils/normalizeWords';
import { downloadCustomCSV } from '../../../helpers/utilityHelper';
import { useIsOnlineContext } from '../../../contexts/IsOnlineContext';
import { sanitizeInput } from '../../../utils/sanitizeInput';

interface ProductPriceComparisonChartProps {
  t: (key: string) => string;
}

const ProductPriceComparisonChart = ({ t }: ProductPriceComparisonChartProps) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [baskets, setBaskets] = useState<Basket[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [municipalities, setMunicipalities] = useState<Location[]>([]);
  const [selectedBasket, setSelectedBasket] = useState<number | undefined>(undefined);
  const [presentations, setPresentations] = useState<any>([]);
  const [selectedPresentations, setSelectedPresentations] = useState<number[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<string | undefined>(undefined);
  const [selectedMunicipalities, setSelectedMunicipalities] = useState<string[] | undefined>();
  const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>([]);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const { isOnline } = useIsOnlineContext();

  const [selectedTimeInterval, setSelectedTimeInterval] = useState<
    'daily' | 'weekly' | 'monthly' | 'yearly'
  >('daily');
  const [isChartdataEmpty, setIsChardataEmpty] = useState<boolean>(false);
  const [chartData, setChartData] = useState<{
    categories: string[];
    series: Array<{
      name: string;
      data: number[];
      color?: string;
    }>;
    timeInterval: string;
  }>({
    categories: [],
    series: [],
    timeInterval: 'daily',
  });
  const [chartLoading, setChartLoading] = useState<boolean>(false);

  const filtersRef = useRef({
    department: '' as string,
    municipalities: [] as string[],
    basket: '' as string,
    presentations: [] as { label: string; value: string }[],
  });

  const getTimePeriodLabels = (timeInterval: string): { previous: string; current: string } => {
    const now = new Date();

    switch (timeInterval) {
      case 'daily': {
        const yesterday = new Date(now);
        yesterday.setDate(now.getDate() - 1);
        const dayBefore = new Date(now);
        dayBefore.setDate(now.getDate() - 2);
        return {
          previous: `${t('dayBefore')} (${dayBefore.toLocaleDateString()})`,
          current: `${t('yesterday')} (${yesterday.toLocaleDateString()})`,
        };
      }

      case 'weekly': {
        const lastWeekStart = new Date(now);
        lastWeekStart.setDate(now.getDate() - now.getDay() - 7);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);

        const thisWeekStart = new Date(now);
        thisWeekStart.setDate(now.getDate() - now.getDay());

        return {
          previous: `${t('lastWeek')} (${lastWeekStart.toLocaleDateString()} - ${lastWeekEnd.toLocaleDateString()})`,
          current: `${t('thisWeek')} (${thisWeekStart.toLocaleDateString()} - ${now.toLocaleDateString()})`,
        };
      }

      case 'monthly': {
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

        return {
          previous: `${t('lastMonth')} (${lastMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })})`,
          current: `${t('thisMonth')} (${thisMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })})`,
        };
      }

      case 'yearly': {
        return {
          previous: `${t('lastYear')} (${now.getFullYear() - 1})`,
          current: `${t('thisYear')} (${now.getFullYear()})`,
        };
      }

      default:
        return {
          previous: 'Previous Period',
          current: 'Current Period',
        };
    }
  };

  const basketItems = baskets.map((basket) => ({
    label: basket.name,
    value: basket.id,
  }));

  const departmentItems = Array.from(new Set(locations.map((location) => location.district))).map(
    (district) => ({
      label: district,
      value: district,
    })
  );

  // const municipalityItems = municipalities.map((municipality) => ({
  //   label: municipality,
  //   value: municipality,
  // }));
  const municipalityItems: any = [
    ...municipalities.map((location) => ({
      label: location.city,
      value: location.city,
    })),
  ];

  const sideFilters = [
    { label: t('daily'), value: 'daily' },
    { label: t('weekly'), value: 'weekly' },
    { label: t('monthly'), value: 'monthly' },
    { label: t('yearly'), value: 'yearly' },
  ];

  const handleDepartmentChange = (department: string) => {
    setSelectedDepartment(department);
    setMunicipalities([]);
    setSelectedMunicipalities(undefined);

    if (department) {
      try {
        const locationIdsForSelectedDepartment = locations
          ?.filter((location) => location?.district === department)
          ?.map((location) => location?.id);

        setSelectedLocationIds(locationIdsForSelectedDepartment);
        const filteredMunicipalities = locations?.filter(
          (location) => location?.district === department
        );
        setMunicipalities(filteredMunicipalities);
      } catch (error) {
        console.error('Failed to load municipalities:', error);
      }
    } else {
      setMunicipalities([]);
      setSelectedMunicipalities(undefined);
      setSelectedLocationIds([]);
    }
  };

  const handleMunicipalityChange = (municipalityArr: any) => {
    if (municipalityArr?.length > 0 && selectedDepartment) {
      const locationIdsForSelectedMunicipalities = locations
        ?.filter(
          (location) =>
            location?.district === selectedDepartment && municipalityArr?.includes(location?.city)
        )
        ?.map((location) => location?.id);

      setSelectedLocationIds(locationIdsForSelectedMunicipalities);
    } else if (municipalityArr?.length > 0) {
      const locationIdsForSelectedMunicipality = locations
        ?.filter((location) => municipalityArr?.includes(location?.city))
        ?.map((location) => location?.id);

      setSelectedLocationIds(locationIdsForSelectedMunicipality);
    } else if (selectedDepartment) {
      const locationIdsForSelectedDepartment = locations
        ?.filter((location) => location?.district === selectedDepartment)
        ?.map((location) => location?.id);

      setSelectedLocationIds(locationIdsForSelectedDepartment);
    } else {
      setSelectedLocationIds([]);
    }

    setSelectedMunicipalities(municipalityArr);
  };

  const handleTimeIntervalChange = (filter: string | number) => {
    setSelectedTimeInterval(filter as 'daily' | 'weekly' | 'monthly' | 'yearly');
  };

  const handleBasketChange = (value: any) => {
    setSelectedBasket(value);
    setSelectedPresentations([]);
  };

  const loadPresentationsForSelection = async () => {
    try {
      const allPresentations = await referenceDataSyncService.getPresentationsForFilters(
        selectedBasket || undefined
      );

      const presentationOptionArr = allPresentations.map((presentation: any) => ({
        label: presentation?.name,
        value: presentation?.id,
        id: presentation?.id,
      }));

      setPresentations(presentationOptionArr);
    } catch (error) {
      console.error('Error loading presentations for selection:', error);
      setPresentations([]);
    }
  };

  useEffect(() => {
    loadPresentationsForSelection();
  }, [selectedBasket]);

  // Dropdown menus replaced with Select components

  const fetchChartData = async () => {
    if (!selectedBasket) {
      return;
    }

    try {
      setChartLoading(true);
      const params = {
        basketId: selectedBasket || undefined,
        locationIds: selectedLocationIds,
        productIds: selectedPresentations || undefined,
        // department: selectedDepartment || undefined,
        // municipality: selectedMunicipalities || undefined,
        timeInterval: selectedTimeInterval,
      };

      const data = await analysisService.getProductAveragePrices(params);

      const timeLabels = getTimePeriodLabels(selectedTimeInterval);
      const updatedSeries = data.series.map((series, index) => ({
        ...series,
        name: index === 0 ? timeLabels.previous : timeLabels.current,
      }));
      const updatedData = {
        ...data,
        series: updatedSeries,
      };
      const dataAvailailityCheck =
        updatedData && updatedData?.series?.every((item) => item?.data?.length === 0);

      setIsChardataEmpty(dataAvailailityCheck);

      setChartData({
        ...data,
        series: updatedSeries,
      });
    } catch (error) {
      console.error('Failed to fetch chart data:', error);
      setIsChardataEmpty(true);
    } finally {
      setChartLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const [basketsData, locationsData] = await Promise.all([
          referenceDataSyncService.getBaskets(),
          referenceDataSyncService.getLocations(),
        ]);

        setBaskets(basketsData as unknown as Basket[]);
        if (selectedBasket == null) {
          setSelectedBasket(await referenceDataSyncService.getBasicFoodBasketId());
        }
        setLocations(locationsData as unknown as Location[]);
      } catch (error) {
        console.error('Failed to load data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    fetchChartData();
  }, [selectedBasket, selectedLocationIds, selectedTimeInterval, selectedPresentations]);

  const chartId = 'chartTitlePriceComparisonProductsChart';

  const downloadPNG = async () => {
    const dataURI = await ApexCharts.exec(chartId, 'dataURI');

    const link = document.createElement('a');
    link.href = dataURI.imgURI;
    link.download = `${t(chartId)}_${Date.now()}.png`;
    link.click();

    await ApexCharts.exec(
      chartId,
      'updateOptions',
      {
        xaxis: { min: currentZoom.current.min, max: currentZoom.current.max },
        annotations: {
          texts: [],
        },
      },
      false,
      true
    );
  };

  const currentZoom = useRef({ min: null, max: null });

  return (
    <div className="chart">
      <section className="mb-2 mt-2 flex w-full flex-col justify-start gap-4 md:mb-16 md:flex-row md:items-center md:justify-between">
        <div className="items-start">
          <h3 className="text-2xl font-semibold">{t('presentations')}</h3>
        </div>
        {!isChartdataEmpty && (
          <div className="flex justify-end gap-3 text-sm md:gap-4">
            <PrimaryButton
              disabled={isDownloading || !isOnline}
              className="px-3"
              handleOnClick={() => {
                if (isDownloading) return;
                setIsDownloading(true);
                try {
                  downloadPNG();
                } finally {
                  setTimeout(() => setIsDownloading(false), 500);
                }
              }}
            >
              {t('downloadPNG')}
            </PrimaryButton>
            <PrimaryButton
              disabled={isDownloading || !isOnline}
              className="px-3"
              handleOnClick={() => {
                if (isDownloading) return;

                if (!filtersRef.current.basket) {
                  const option = basketItems.find((o) => o.value === selectedBasket);
                  filtersRef.current.basket = option?.label as string;
                }

                const filterData = [
                  {
                    key: 'basket',
                    label: t('basket'),
                    value: filtersRef.current.basket,
                  },
                  {
                    key: 'presentations',
                    label: t('presentations'),
                    value: filtersRef.current.presentations.map((a) => a.label),
                  },
                  { key: 'department', label: t('department'), value: selectedDepartment },
                  {
                    key: 'municipalities',
                    label: t('downloadFilters.municipalities'),
                    value: filtersRef.current.municipalities,
                  },
                  {
                    key: 'timeInterval',
                    label: t('timeInterval'),
                    value: t(`${selectedTimeInterval}`),
                  },
                ];

                setIsDownloading(true);
                try {
                  const timeLabels = getTimePeriodLabels(selectedTimeInterval);
                  const translatedChartData = {
                    ...chartData,
                    series: chartData.series.map((s: any, index: number) => ({
                      ...s,
                      name: index === 0 ? timeLabels.previous : timeLabels.current,
                    }))
                  };
                  downloadCustomCSV(
                    t('chartTitlePriceComparisonProductsChart'),
                    translatedChartData,
                    filterData,
                    t,
                    t('presentations')
                  );
                } finally {
                  setTimeout(() => setIsDownloading(false), 500);
                }
              }}
            >
              {t('downloadCSV')}
            </PrimaryButton>
          </div>
        )}
      </section>

      <div className="mb-6 mt-4 flex w-full flex-col items-start gap-6 overflow-visible md:flex-row md:flex-wrap lg:flex-wrap lg:items-center xl:flex-nowrap">
        {/* <p>{t('filters')}:</p> */}
        <FormItem
          className="config mb-0 w-full min-w-[200px] flex-initial lg:w-1/4"
          layout="vertical"
          label={<span className="text-sm font-medium text-gray-700">{t('basket')}</span>}
        >
          <Select
            value={selectedBasket}
            options={basketItems}
            placeholder={t('basket')}
            // allowClear
            showSearch
            onChange={(val, option) => {
              filtersRef.current.basket = (option as { label: string; value: number }).label;
              handleBasketChange(val);
            }}
            filterOption={(input, option) =>
              normalize(option?.label ?? '')
                .toString()
                .toLowerCase()
                .includes(normalize(input.toLowerCase()))
            }
            className="w-[144px] [&>.ant-select-selector]:!rounded-lg"
          />
        </FormItem>
        <FormItem
          className="config mb-0 w-full min-w-[200px] flex-initial lg:w-1/4"
          layout="vertical"
          label={<span className="text-sm font-medium text-gray-700">{t('presentations')}</span>}
        >
          <Select
            value={selectedPresentations}
            options={presentations}
            placeholder={t('presentationsPlaceholder')}
            allowClear
            onClear={() => setSelectedPresentations([])}
            showSearch
            mode="multiple"
            maxTagCount={1}
            maxTagTextLength={window.matchMedia('(min-width: 1600px)').matches ? 20 : 10}
            onChange={(val, options) => {
              filtersRef.current.presentations = options as any;
              setSelectedPresentations(val);
            }}
            filterOption={(input, option) =>
              (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
            }
            className="w-[144px] [&>.ant-select-selector]:!rounded-lg"
          />
        </FormItem>
        <FormItem
          className="config mb-0 w-full min-w-[200px] flex-initial lg:w-1/4"
          layout="vertical"
          label={<span className="text-sm font-medium text-gray-700">{t('department')}</span>}
        >
          <Select
            value={selectedDepartment}
            options={departmentItems}
            placeholder={t('departmentPlaceholder')}
            allowClear
            onClear={() => handleDepartmentChange}
            showSearch
            onChange={(val) => {
              filtersRef.current.department = val;
              handleDepartmentChange(val);
            }}
            className="w-[144px] [&>.ant-select-selector]:!rounded-lg"
          />
        </FormItem>
        <FormItem
          className="config mb-0 w-full min-w-[200px] flex-initial lg:w-1/4"
          layout="vertical"
          label={<span className="text-sm font-medium text-gray-700">{t('municipality')}</span>}
        >
          <Select
            value={selectedMunicipalities}
            options={municipalityItems}
            placeholder={t('municipalityPlaceholder')}
            allowClear
            onClear={() => setSelectedMunicipalities(undefined)}
            mode="multiple"
            maxTagCount={1}
            maxTagTextLength={window.matchMedia('(min-width: 1600px)').matches ? 20 : 10}
            disabled={!selectedDepartment}
            onChange={(val) => {
              filtersRef.current.municipalities = val;
              handleMunicipalityChange(val);
            }}
            className="w-[144px] [&>.ant-select-selector]:!rounded-lg"
          />
        </FormItem>
      </div>

      <div className="flex flex-col justify-end lg:flex-row">
        <div className="mt-4 lg:mt-0">
          <Select
            popupClassName="!w-[150px]"
            className="w-[max(fit-content,120px)] [&>.ant-select-selector]:!border-none [&>.ant-select-selector]:!pl-0 [&_.ant-select-selection-item]:!text-base [&_.ant-select-selection-item]:!text-black [&_.ant-select-selection-placeholder]:!text-base [&_.ant-select-selection-placeholder]:!text-black [&_.ant-select-selection-placeholder]:hover:!text-black/50 [&_input]:!capitalize"
            options={sideFilters.map((filter) => ({
              label: filter.label,
              value: filter.value,
            }))}
            defaultValue={sideFilters[0].value}
            placeholder={t('select')}
            onChange={(value) => handleTimeIntervalChange(value)}
            value={selectedTimeInterval}
          />
        </div>
      </div>

      {isChartdataEmpty ? (
        <div key="no-data" className="mb-6 py-8 text-center text-sm text-gray-500">
          {t('noDataAvailable')}
        </div>
      ) : (
        <ChartComponent
          title={''}
          type={CHART_TYPES.BAR}
          series={chartData?.series?.map((s) => ({ ...s, name: sanitizeInput(s?.name) }))}
          options={{
            chart: {
              id: chartId,
              toolbar: { show: false },
              events: {
                zoomed(_chart, { xaxis }) {
                  currentZoom.current = xaxis;
                },
              },
            },
            plotOptions: {
              bar: {
                horizontal: false,
                columnWidth: '55%',
              },
            },
            dataLabels: {
              enabled: false,
            },
            xaxis: {
              categories: chartData?.categories?.map(sanitizeInput),
              labels: {
                trim: true,
                // formatter: (val) => val.length > 15 ? `${val.substring(0,15)}...` : val,
              },
            },
            yaxis: {
              title: {
                text: t('priceYaxis'),
              },
              labels: {
                formatter: (val: number) => {
                  return typeof val === 'number' ? val.toFixed(2) : val;
                },
              },
            },
            tooltip: {
              y: {
                formatter: (val: number) => {
                  return typeof val === 'number' ? val.toFixed(2) : val;
                },
              },
            },
            colors: ['#00b050', '#ff0000', '#0070c0', '#ff9900', '#9933cc', '#00cc99'],
            legend: {
              position: 'bottom',
              showForSingleSeries: true,
            },
            grid: {
              borderColor: '#e7e7e7',
              strokeDashArray: 4,
            },
          }}
          maxHeight={400}
          // topWidgets={

          // }
          loading={loading || chartLoading}
        />
      )}
    </div>
  );
};

export default ProductPriceComparisonChart;
